import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { languageCodes, defaultLanguage } from "./i18n";
import LanguageRouter from "./components/LanguageRouter";
import { trackPageView } from "./lib/analytics";

// Scroll to top on route change + track page views
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Track page view for analytics
    trackPageView(pathname);
    // Tag <body> with a scope so global CSS can flatten cards site-wide
    // (opt-out for the News surface which keeps its editorial layout)
    try {
      const isNewsRoute = /^\/(?:[a-z]{2}\/)?news(\/|$)/.test(pathname);
      document.body.dataset.route = isNewsRoute ? 'news' : 'flat';
    } catch (_) { /* noop */ }
  }, [pathname]);

  return null;
}

// Eagerly loaded (always needed)
import HomePage from "./pages/HomePage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppChatBadge from "./components/WhatsAppChatBadge";

// Lazy-loaded pages (only loaded when navigated to)
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BrokersPage = lazy(() => import("./pages/BrokersPage"));
const BrokerDetailPage = lazy(() => import("./pages/BrokerDetailPage"));
const BrokerDirectoryPage = lazy(() => import("./pages/BrokerDirectoryPage"));
const ChartsPage = lazy(() => import("./pages/FxAstraChartsPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const WidgetsPage = lazy(() => import("./pages/WidgetsPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./pages/NewsDetailPage"));
const ToolboxPage = lazy(() => import("./pages/ToolboxPage"));
const TradersPage = lazy(() => import("./pages/TradersPage"));
const TraderProfilePage = lazy(() => import("./pages/TraderProfilePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PostEditorPage = lazy(() => import("./pages/PostEditorPage"));
const WarningsPage = lazy(() => import("./pages/WarningsPage"));
const BrokerClaimSuccessPage = lazy(() => import("./pages/BrokerClaimSuccessPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const AwardsPage = lazy(() => import("./pages/AwardsPage"));
const NomineeDetailPage = lazy(() => import("./pages/NomineeDetailPage"));
const SetupBrokerPage = lazy(() => import("./pages/SetupBrokerPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const FounderPage = lazy(() => import("./pages/FounderPage"));
const TournamentPage = lazy(() => import("./pages/TournamentPage"));
const AnalysisFeedPage = lazy(() => import("./pages/AnalysisFeedPage"));
const LegalPages = lazy(() => import("./pages/LegalPages"));

// Lazy wrappers for named exports from LegalPages
const PrivacyPolicyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.TermsOfServicePage })));
const RiskDisclosurePage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.RiskDisclosurePage })));
const CookiePolicyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.CookiePolicyPage })));
const AnalysisPostPage = lazy(() => import("./pages/AnalysisFeedPage").then(m => ({ default: m.AnalysisPostPage })));
const EducationPage = lazy(() => import("./pages/EducationPage"));
const FreeCoursePage = lazy(() => import("./pages/FreeCoursePage"));
const SignalsMarketplacePage = lazy(() => import("./pages/SignalsPage"));
const SignalDetailPage = lazy(() => import("./pages/SignalsPage").then(m => ({ default: m.SignalDetailPage })));
const AstraTradingTerminal = lazy(() => import("./pages/AstraTradingTerminal"));
const AstraPlatformPage = lazy(() => import("./pages/AstraPlatformPage"));
const StandaloneTradingPage = lazy(() => import("./pages/StandaloneTradingPage"));
const ChartWidget = lazy(() => import("./pages/ChartWidget"));
const LandingPageViewer = lazy(() => import("./pages/LandingPageViewer"));
const CourseDetailPage = lazy(() => import("./pages/EducationCoursePage").then(m => ({ default: m.CourseDetailPage })));
const LessonViewerPage = lazy(() => import("./pages/EducationCoursePage").then(m => ({ default: m.LessonViewerPage })));

// ============= ASTRA TRADING PLATFORM (White-Label) =============
// These routes belong to the separate trading platform product.
// When deployed independently, these move to their own App.js.
const BrokerAdminLayout = lazy(() => import("./platform/admin/AdminLayout"));
const BrokerAdminLogin = lazy(() => import("./platform/admin/AdminLogin"));
const BrokerDashboard = lazy(() => import("./platform/admin/Dashboard"));
const BrokerClients = lazy(() => import("./platform/admin/ClientsPage"));
const BrokerTrading = lazy(() => import("./platform/admin/TradingMonitor"));
const BrokerDeposits = lazy(() => import("./platform/admin/DepositsPage"));
const BrokerKycReview = lazy(() => import("./platform/admin/KycReviewPage"));
const BrokerGroups = lazy(() => import("./platform/admin/GroupsPage"));
const BrokerLandingPages = lazy(() => import("./platform/admin/LandingPagesPage"));
const BrokerPageBuilder = lazy(() => import("./platform/admin/PageBuilder"));
const BrokerBranding = lazy(() => import("./platform/admin/BrandingPage"));
const BrokerDomains = lazy(() => import("./platform/admin/DomainsPage"));
const BrokerTeam = lazy(() => import("./platform/admin/TeamPage"));
const BrokerIB = lazy(() => import("./platform/admin/IBPage"));
const BrokerMessaging = lazy(() => import("./platform/admin/MessagingPage"));
const BrokerSocialTrading = lazy(() => import("./platform/admin/SocialTradingPage"));
const BrokerExecutionModel = lazy(() => import("./platform/admin/ExecutionModelPage"));
const BrokerRiskManagement = lazy(() => import("./platform/admin/RiskManagementPage"));
const BrokerInstruments = lazy(() => import("./platform/admin/InstrumentsPage"));
const BrokerIntegrations = lazy(() => import("./platform/admin/IntegrationsPage"));
const ClientLayout = lazy(() => import("./platform/client/ClientLayout"));
const ClientLogin = lazy(() => import("./platform/client/ClientAuth").then(m => ({ default: m.ClientLogin })));
const ClientRegister = lazy(() => import("./platform/client/ClientAuth").then(m => ({ default: m.ClientRegister })));
const ClientDashboard = lazy(() => import("./platform/client/ClientDashboard"));
const ClientAccounts = lazy(() => import("./platform/client/AccountsPage"));
const ClientDeposit = lazy(() => import("./platform/client/DepositPage"));
const ClientKyc = lazy(() => import("./platform/client/KycPage"));
const ClientDownloads = lazy(() => import("./platform/client/DownloadsPage"));
const ClientInbox = lazy(() => import("./platform/client/InboxPage"));
const ClientSocialTrading = lazy(() => import("./platform/client/SocialTradingPage"));
const ClientInvestments = lazy(() => import("./platform/client/InvestmentsPage"));

// Minimal loading fallback
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="h-8 w-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Pages that should NOT have navbar/footer (standalone layouts)
const standalonePages = ['/dashboard', '/admin', '/platform', '/astratradingterminal', '/trading', '/widget/', '/lp'];

// All application routes - defined once and reused
const appRoutes = [
  { path: "/", element: <HomePage /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/brokers", element: <BrokersPage /> },
  { path: "/brokers/directory", element: <BrokerDirectoryPage /> },
  { path: "/brokers/:id", element: <BrokerDetailPage /> },
  { path: "/charts", element: <ChartsPage /> },
  { path: "/calendar", element: <CalendarPage /> },
  { path: "/widgets", element: <WidgetsPage /> },
  { path: "/news", element: <NewsPage /> },
  { path: "/news/article/:articleId", element: <NewsDetailPage /> },
  { path: "/toolbox", element: <ToolboxPage /> },
  { path: "/traders", element: <TradersPage /> },
  { path: "/traders/:traderId", element: <TraderProfilePage /> },
  { path: "/auth", element: <AuthPage /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/dashboard/posts/new", element: <PostEditorPage /> },
  { path: "/dashboard/posts/:id/edit", element: <PostEditorPage /> },
  { path: "/warnings", element: <WarningsPage /> },
  { path: "/broker/claim/success", element: <BrokerClaimSuccessPage /> },
  { path: "/login", element: <AuthPage /> },
  { path: "/register", element: <AuthPage /> },
  { path: "/payment/success", element: <PaymentSuccessPage /> },
  { path: "/payment/cancel", element: <PaymentCancelPage /> },
  { path: "/awards", element: <AwardsPage /> },
  { path: "/awards/nominee/:id", element: <NomineeDetailPage /> },
  { path: "/tournament", element: <TournamentPage /> },
  { path: "/setup-broker", element: <SetupBrokerPage /> },
  { path: "/admin", element: <AdminPage /> },
  { path: "/admin/login", element: <AdminLoginPage /> },
  { path: "/contact", element: <ContactPage /> },
  { path: "/careers", element: <CareersPage /> },
  { path: "/press", element: <PressPage /> },
  { path: "/amiran-azaladze", element: <FounderPage /> },
  { path: "/legal/privacy", element: <PrivacyPolicyPage /> },
  { path: "/legal/terms", element: <TermsOfServicePage /> },
  { path: "/legal/risk", element: <RiskDisclosurePage /> },
  { path: "/legal/cookies", element: <CookiePolicyPage /> },
  { path: "/analysis", element: <AnalysisFeedPage /> },
  { path: "/analysis/:postId", element: <AnalysisPostPage /> },
  { path: "/signals", element: <SignalsMarketplacePage /> },
  { path: "/signals/:signalId", element: <SignalDetailPage /> },
  { path: "/education", element: <EducationPage /> },
  { path: "/education/course/free-trading-101", element: <FreeCoursePage /> },
  { path: "/education/course/:courseId", element: <CourseDetailPage /> },
  { path: "/education/lesson/:lessonId", element: <LessonViewerPage /> },
  { path: "/astratradingterminal", element: <AstraTradingTerminal /> },
  { path: "/astra-platform", element: <AstraPlatformPage /> },
  { path: "/trading", element: <StandaloneTradingPage /> },
  { path: "/widget/chart", element: <ChartWidget /> },
  { path: "/lp/:slug", element: <LandingPageViewer /> },
];

function AppLayout() {
  const location = useLocation();

  // Heartbeat: ping the backend every 60s while the user is logged in & tab visible
  // → keeps `last_seen_at` fresh so the LIVE indicator stays green for online users.
  React.useEffect(() => {
    let timer = null;
    const ping = () => {
      const tok = localStorage.getItem('fx_token') || localStorage.getItem('token');
      if (!tok || document.visibilityState !== 'visible') return;
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/heartbeat';
      fetch(url, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + tok },
        keepalive: true,
      }).catch(() => {});
    };
    ping();
    timer = setInterval(ping, 60_000);
    document.addEventListener('visibilitychange', ping);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', ping);
    };
  }, []);
  
  // Check if current path is standalone (with or without language prefix)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];
  const isLangPrefix = languageCodes.includes(firstPart);
  const checkPath = isLangPrefix ? `/${pathParts.slice(1).join('/')}` : location.pathname;
  const isStandalonePage = standalonePages.some(path => checkPath.startsWith(path));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ScrollToTop />
      {!isStandalonePage && <Navbar />}
      
      <main className={isStandalonePage ? "" : "flex-1"}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Default routes (English - no prefix) */}
            <Route element={<LanguageRouter />}>
              {appRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
            </Route>
            
            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageRouter />}>
              {appRoutes.map(({ path, element }) => (
                <Route 
                  key={`lang-${path}`} 
                  path={path === "/" ? "" : path.slice(1)} 
                  element={element} 
                />
              ))}
            </Route>

            {/* Platform - Broker Admin */}
            <Route path="/platform/admin/login" element={<BrokerAdminLogin />} />
            <Route path="/platform/admin" element={<BrokerAdminLayout />}>
              <Route index element={<BrokerDashboard />} />
              <Route path="clients" element={<BrokerClients />} />
              <Route path="trading" element={<BrokerTrading />} />
              <Route path="deposits" element={<BrokerDeposits />} />
              <Route path="kyc" element={<BrokerKycReview />} />
              <Route path="groups" element={<BrokerGroups />} />
              <Route path="pages" element={<BrokerLandingPages />} />
              <Route path="pages/:pageId" element={<BrokerPageBuilder />} />
              <Route path="branding" element={<BrokerBranding />} />
              <Route path="domains" element={<BrokerDomains />} />
              <Route path="team" element={<BrokerTeam />} />
              <Route path="ib" element={<BrokerIB />} />
              <Route path="messaging" element={<BrokerMessaging />} />
              <Route path="social-trading" element={<BrokerSocialTrading />} />
              <Route path="execution" element={<BrokerExecutionModel />} />
              <Route path="risk" element={<BrokerRiskManagement />} />
              <Route path="instruments" element={<BrokerInstruments />} />
              <Route path="integrations" element={<BrokerIntegrations />} />
            </Route>

            {/* Platform - Client Portal */}
            <Route path="/platform/client/login" element={<ClientLogin />} />
            <Route path="/platform/client/register" element={<ClientRegister />} />
            <Route path="/platform/client" element={<ClientLayout />}>
              <Route index element={<ClientDashboard />} />
              <Route path="accounts" element={<ClientAccounts />} />
              <Route path="deposit" element={<ClientDeposit />} />
              <Route path="kyc" element={<ClientKyc />} />
              <Route path="downloads" element={<ClientDownloads />} />
              <Route path="inbox" element={<ClientInbox />} />
              <Route path="social-trading" element={<ClientSocialTrading />} />
              <Route path="investments" element={<ClientInvestments />} />
            </Route>
            
            {/* Fallback - redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      
      {!isStandalonePage && <Footer />}

      {/* Floating WhatsApp live chat — visible site-wide on all pages */}
      <WhatsAppChatBadge />

      <Toaster 
        position="bottom-right" 
        offset={96}
        duration={3000}
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
